'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import type { GameCategory } from '@/lib/products';
import { useI18n } from '@/i18n/LocaleProvider';
import { localizeCategory } from '@/i18n/products';

interface CategoryBentoGridProps {
  categories: GameCategory[];
}

export default function CategoryBentoGrid({ categories: baseCategories }: CategoryBentoGridProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const { locale, t } = useI18n();
  const categories = baseCategories.map(cat => localizeCategory(cat, locale));

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = section.querySelectorAll('.stagger-child');
    children.forEach((child, i) => {
      (child as HTMLElement).style.transitionDelay = `${i * 80}ms`;
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, []);

  // BENTO MAP (7 cards, 4-col grid):
  // Row 1: [col-1-2: Pokémon cs-2 rs-2] [col-3: OnePiece] [col-4: Lorcana]
  // Row 2: [col-1-2: (Pokémon cont)] [col-3: Riftbound] [col-4: DragonBall]
  // Row 3: [col-1-2: Cyberpunk cs-2] [col-3-4: OtherTCGs cs-2]
  // Placed 7/7 ✓

  const [pokemon, onePiece, lorcana, riftbound, dragonBall, cyberpunk, other] = categories;

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Section header */}
      <div className="stagger-child reveal-section mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold tracking-widest uppercase text-accent mb-2 block">{t.bento.eyebrow}</span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
            {t.bento.title}
          </h2>
        </div>
        <p className="text-muted-foreground text-sm max-w-xs leading-relaxed">
          {t.bento.subtitle}
        </p>
      </div>

      {/* Bento Grid — desktop */}
      <div className="hidden md:grid grid-cols-4 gap-4 auto-rows-[220px]">
        {/* Pokémon — col-span-2, row-span-2 */}
        <CategoryCard category={pokemon} className="col-span-2 row-span-2 stagger-child" large />
        {/* One Piece */}
        <CategoryCard category={onePiece} className="col-span-1 stagger-child" />
        {/* Lorcana */}
        <CategoryCard category={lorcana} className="col-span-1 stagger-child" />
        {/* Riftbound */}
        <CategoryCard category={riftbound} className="col-span-1 stagger-child" />
        {/* Dragon Ball */}
        <CategoryCard category={dragonBall} className="col-span-1 stagger-child" />
        {/* Cyberpunk — col-span-2 */}
        <CategoryCard category={cyberpunk} className="col-span-2 stagger-child" />
        {/* Other TCGs — col-span-2 */}
        <CategoryCard category={other} className="col-span-2 stagger-child" />
      </div>

      {/* Mobile grid */}
      <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
        {categories.map(cat => (
          <CategoryCard key={cat.id} category={cat} className="stagger-child" />
        ))}
      </div>
    </section>
  );
}

function CategoryCard({ category, className = '', large = false }: { category: GameCategory; className?: string; large?: boolean }) {
  const { t } = useI18n();
  return (
    <Link
      href={`/category?game=${category.slug}`}
      className={`category-card group relative overflow-hidden rounded-2xl glass-card border border-border hover:border-primary/40 transition-all duration-500 ${className}`}
    >
      {/* Gradient background — uses CSS vars so it adapts to light/dark */}
      <div
        className="absolute inset-0 transition-opacity duration-500 category-card-gradient"
        style={{ background: `linear-gradient(135deg, ${category.color}28 0%, var(--card) 70%)` }}
      />
      <div className="absolute inset-0 category-card-overlay" />

      {/* Color accent line */}
      <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(90deg, transparent, ${category.color}, transparent)` }} />

      {/* Official Logo — centered */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-8">
        <div className="category-card-logo opacity-90 group-hover:opacity-100 transition-opacity duration-500 w-full flex items-center justify-center">
          {category.image ? (
            <AppImage
              src={category.image}
              alt={category.imageAlt}
              width={200}
              height={120}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-contain drop-shadow-lg max-w-full max-h-[120px]"
            />
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-between p-5">
        <div className="flex items-start justify-between">
          {/* Color dot */}
          <div className="w-3 h-3 rounded-full border-2 border-white/20" style={{ backgroundColor: category.color }} />
          {/* Arrow */}
          <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0" style={{ backgroundColor: `${category.color}20` }}>
            <svg className="w-3.5 h-3.5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" />
            </svg>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold tracking-widest uppercase mb-1.5" style={{ color: category.color }}>
            {t.common.productCount(category.productCount)}
          </p>
          <h3 className={`font-extrabold tracking-tight text-foreground leading-tight mb-2 ${large ? 'text-3xl md:text-4xl' : 'text-xl'}`}>
            {category.shortName}
          </h3>
          {large && (
            <p className="text-sm text-muted-foreground leading-relaxed hidden sm:block max-w-xs">
              {category.description}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}